import { _ } from "../../polyglot/polyglot.js";
import {
	formatAsMonthAndYear,
	formatAsNumeralDate,
} from "../../helpers/date-formatting-util.js";
import { widget } from "@foretagsplatsen/widgetjs";
import optionsBag from "../../../models/options/optionsBag.js";
import popoverWidget from "../popoverWidget/popoverWidget.js";

/**
 * Displays a progress bar showing an overview of a fiscal year.
 *
 * The progress bar is in one of these 3 states:
 *
 * - waiting (fiscalYear===`undefined`): the widget shows a
 *	 temporary progress bar indicating that no fiscal year is
 *	 available yet.
 *
 * - completed (fiscalYear is an object): the widget shows
 *	 `fiscalYear`.
 *
 * - empty (fiscalYear===null): there is no fiscal year to show
 * (maybe because the company has none)
 *
 * @param {{}} spec - Parameter object
 * @param {fiscalYear} [spec.fiscalYear] - The fiscal year for
 * which the progress is displayed
 * @param {{}} my - protected fields holder
 * @return {*}
 */
function fiscalYearProgressBarWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	let fiscalYear = spec.fiscalYear || undefined;

	that.onToggleFiscalYearEditor = my.events.createEvent();

	my.popover = popoverWidget({
		popoverCssClass: "popover popover-condensed fade top",
		header: function (html) {
			html.strong(
				_("Fiscal Year {year}", {
					year: my.getName(),
				}),
			);
		},
		content: renderPopoverContentOn,
		primaryLabel: _("Set last active month"),
		primaryAction: toggleFiscalYearEditor,
	});

	that.renderContentOn = function (html) {
		html.div({ klass: "fiscal-year-progress-bar" }, (html) => {
			if (that.isWaiting()) {
				that.renderWaitingOn(html);
			} else if (that.isCompleted()) {
				that.renderCompletedOn(html);
			} else {
				that.renderEmptyOn(html);
			}
		});
	};

	that.renderWaitingOn = function (html) {
		html.div(
			{
				class: "progress progress-striped active progress-stacked progress-admin-form fiscal-year-progress-bar-waiting",
			},
			html.div({
				class: "progress-bar progress-bar-primary",
				role: "progressbar",
				"aria-valuenow": "100",
				"aria-valuemin": "0",
				"aria-valuemax": "100",
				style: "width: 100%;",
			}),
		);
	};

	that.renderEmptyOn = function (html) {
		html.div(
			{ klass: "fiscal-year-progress-bar-empty" },
			html.span(_("No available fiscal year")),
		);
	};

	that.renderCompletedOn = function (html) {
		const popoverTrigger = html.div(
			{ klass: "fiscal-year-progress-bar-completed" },
			html.div(
				{
					class: "progress progress-stacked progress-admin-form nomargin-bottom width-full",
				},
				html.div({
					class: [
						"progress-bar",
						"progress-bar-primary",
						{ "progress-closed": my.getActiveProgress() === 100 },
					],
					role: "progressbar",
					"aria-valuenow": my.getActiveProgress(),
					"aria-valuemin": "0",
					"aria-valuemax": "100",
					style: `width: ${my.getActiveProgress()}%;`,
				}),
				(html) => {
					if (!shouldShowInactivePeriod()) {
						return;
					}

					html.div({
						class: "progress-bar progress-bar-secondary progress-bar-stripped",
						role: "progressbar",
						"aria-valuenow": my.getInactiveProgress(),
						"aria-valuemin": "0",
						"aria-valuemax": "100",
						style: `width: ${my.getInactiveProgress()}%;`,
					});
				},
			),
			html.p(
				{ klass: "fiscal-year-dates font-light" },
				formatAsNumeralDate(fiscalYear.getStart()),
				html.span(
					{ klass: "last-active-day" },
					formatAsNumeralDate(fiscalYear.getEnd()),
				),
			),
		);

		optionsBag.fromId("active-period").whenActive(() => {
			my.popover.setTriggerItem(popoverTrigger);
		});
	};

	function renderPopoverContentOn(html) {
		html.dl(
			{ klass: "dl-horizontal" },
			html.dt(_("Imported data until")),
			html.dd(formatAsNumeralDate(my.getLastTransactionDate())),
			(html) => {
				if (!shouldShowInactivePeriod()) {
					return;
				}
				html.dt(_("Last active month"));
				if (my.isActive()) {
					html.dd(
						formatAsMonthAndYear(my.getLastActiveTransactionDate()),
					);
				} else {
					html.dd(_("No active month"));
				}
			},
		);
	}

	that.setFiscalYear = function (newFiscalYear) {
		fiscalYear = newFiscalYear;
		that.update();
	};

	my.getFromFiscalYear = function (getter, defaultValue) {
		defaultValue ||= 0;

		if (that.isWaiting() || that.isEmpty()) {
			return defaultValue;
		}
		return fiscalYear[getter]();
	};

	my.getName = function () {
		return my.getFromFiscalYear("getName", "");
	};

	my.getLastTransactionDate = function () {
		return my.getFromFiscalYear("getLastTransactionDate", 0);
	};

	my.isActive = function () {
		return my.getFromFiscalYear("isActive", false);
	};

	my.getLastActiveTransactionDate = function () {
		return my.getFromFiscalYear("getLastActiveTransactionDate", 0);
	};

	my.getActiveProgress = function () {
		return my.getFromFiscalYear("getActiveProgress", 100);
	};

	my.getInactiveProgress = function () {
		return my.getFromFiscalYear("getInactiveProgress", 100);
	};

	that.isWaiting = function () {
		return fiscalYear === undefined;
	};

	that.isEmpty = function () {
		return fiscalYear === null;
	};

	that.isCompleted = function () {
		return !that.isEmpty() && !that.isWaiting();
	};

	function shouldShowInactivePeriod() {
		return optionsBag
			.fromId("active-period")
			.whenActive(fiscalYear.hasInactiveTransactions());
	}

	function toggleFiscalYearEditor() {
		that.onToggleFiscalYearEditor.trigger();
	}

	return that;
}

export default fiscalYearProgressBarWidget;
